/**
 * FamilyMarketplace — second demand source for families without a
 * hospital referral. Calm-but-practical:
 *   1. Family fills a request (service, state, budget, basic patient info)
 *   2. Server returns ranked candidates with trust info
 *   3. Family selects one → opens a placement in the SAME spine
 *   4. From there the existing spine handles match/accept/visit/payout
 *
 * Gated by `family_marketplace` entitlement. Default-OFF.
 * Only `private_pay` funding is live in this pass.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, MapPin, Stethoscope, AlertTriangle, Loader2, Lock,
  ChevronRight, ExternalLink, Sparkles, DollarSign, X, Inbox, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrgEntitlements } from '../hooks/useOrgEntitlements';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
const SERVICES = [
  { v: 'HOME_HEALTH_RN', l: 'Home-health nurse (RN)' },
  { v: 'HOME_HEALTH_LPN', l: 'Home-health LPN' },
  { v: 'PT', l: 'Physical therapy' },
  { v: 'OT', l: 'Occupational therapy' },
  { v: 'ST', l: 'Speech therapy' },
  { v: 'PERSONAL_CARE', l: 'Personal care aide' },
];

const CredentialingPill = ({ status, label }) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-800',
    in_review: 'bg-amber-100 text-amber-800',
    draft: 'bg-slate-100 text-slate-700',
    expired: 'bg-red-100 text-red-800',
    suspended: 'bg-red-100 text-red-800',
    revoked: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}>
      <ShieldCheck className="w-3 h-3" /> {label}
    </span>
  );
};

export default function FamilyMarketplace() {
  const { loading: entLoading, hasFlag } = useOrgEntitlements();
  if (entLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!hasFlag('family_marketplace')) {
    return <MarketplaceLocked />;
  }
  return <MarketplaceInner />;
}

const MarketplaceLocked = () => (
  <div className="min-h-[60vh] flex items-center justify-center px-6"
        data-testid="family-marketplace-locked">
    <div className="max-w-xl text-center space-y-6">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100">
        <Lock className="w-7 h-7 text-slate-500" />
      </div>
      <h1 className="text-3xl font-semibold text-slate-900">
        The public marketplace isn&apos;t open in your area yet.
      </h1>
      <p className="text-base text-slate-600 leading-relaxed">
        We open the family marketplace per-market, only once enough
        providers are signed up in your state to fill requests quickly.
        An empty marketplace would burn your trust.
      </p>

      {/* Iter-116 — instead of a dead "coming soon" screen we route the
          family into the live AI-matching flow. This is the same discipline-
          scoped credentialed-clinician search we use post-referral, but
          initiated by the family. Zero-list rendering isn't possible here
          because the AI ranks by geography + acuity + service. */}
      <div className="pt-2">
        <Link
          to="/patient/find-clinicians"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cn-primary text-white text-sm font-semibold hover:bg-cn-primary/90 shadow-sm"
          data-testid="family-marketplace-locked-ai-match-cta"
        >
          <Sparkles className="w-4 h-4" /> Try AI matching instead
        </Link>
        <p className="mt-3 text-xs text-slate-500">
          We&apos;ll match you with credentialed clinicians in your area — no
          browsing an empty marketplace.
        </p>
      </div>

      <div className="pt-1 border-t border-slate-200 mt-4">
        <a
          href="mailto:ops@caraloom.com?subject=Family%20Marketplace%20Waitlist"
          className="inline-flex items-center gap-2 mt-4 text-xs font-medium text-slate-500 hover:text-slate-800 underline underline-offset-4"
          data-testid="family-marketplace-locked-waitlist"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Or join the marketplace waitlist
        </a>
      </div>
    </div>
  </div>
);

function MarketplaceInner() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFmr, setActiveFmr] = useState(null);
  const [newRequestOpen, setNewRequestOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/family/match-requests`,
        { headers: { Authorization: `Bearer ${token}` } });
      setRequests(res.data.rows || []);
    } catch {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const select = async (fmrId, clinicianId) => {
    try {
      await axios.post(`${API}/family/match-requests/${fmrId}/select-provider`,
        { clinician_id: clinicianId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Provider selected — request is now in the matching pipeline');
      setActiveFmr(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Selection failed');
    }
  };

  const cancel = async (fmrId) => {
    if (!confirm('Cancel this request?')) return;
    try {
      await axios.post(`${API}/family/match-requests/${fmrId}/cancel`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cancelled');
      load();
    } catch {
      toast.error('Cancel failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8" data-testid="family-marketplace">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Find a provider</h1>
          <p className="text-sm text-slate-500 mt-1">
            Transparent pricing. Verified clinicians. You stay in control.
          </p>
        </div>
        <button onClick={() => setNewRequestOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                data-testid="new-request-btn">
          <Sparkles className="w-4 h-4" /> New request
        </button>
      </div>

      {/* Requests list */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No requests yet. Tap <strong>New request</strong> to start.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {requests.map((r) => (
              <li key={r.id} className="p-5 flex items-start gap-4"
                  data-testid={`request-row-${r.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusPill status={r.status} />
                    <span className="text-xs text-slate-500">
                      {r.ordered_services.join(', ')}
                    </span>
                  </div>
                  <div className="text-base text-slate-900 font-medium">
                    {r.state} · {r.urgency}
                    {r.private_pay_budget_cents && (
                      <span className="ml-2 text-sm text-slate-500">
                        · Budget ${(r.private_pay_budget_cents / 100).toFixed(0)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {r.candidates?.length || 0} candidate{r.candidates?.length === 1 ? '' : 's'} · created {new Date(r.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.status === 'open' && r.candidates?.length > 0 && (
                    <button onClick={() => setActiveFmr(r)}
                            className="text-sm px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                            data-testid={`view-candidates-${r.id}`}>
                      View candidates <ChevronRight className="w-3 h-3 inline ml-1" />
                    </button>
                  )}
                  {r.status === 'open' && (
                    <button onClick={() => cancel(r.id)}
                            className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                            data-testid={`cancel-${r.id}`}>
                      Cancel
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {newRequestOpen && (
        <NewRequestSheet
          onClose={() => setNewRequestOpen(false)}
          onCreated={() => { setNewRequestOpen(false); load(); }}
          token={token}
        />
      )}

      {activeFmr && (
        <CandidatesSheet
          fmr={activeFmr}
          onClose={() => setActiveFmr(null)}
          onSelect={(cid) => select(activeFmr.id, cid)}
          token={token}
        />
      )}

      <p className="text-xs text-slate-400 text-center">
        Live in select markets · Provider safety reports go to ops within 1 business day
      </p>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    open:               { tone: 'bg-blue-100 text-blue-800',       label: 'Open' },
    provider_selected:  { tone: 'bg-amber-100 text-amber-800',     label: 'Provider selected' },
    placed:             { tone: 'bg-emerald-100 text-emerald-800', label: 'In care' },
    cancelled:          { tone: 'bg-slate-100 text-slate-700',     label: 'Cancelled' },
    closed:             { tone: 'bg-slate-100 text-slate-700',     label: 'Closed' },
  };
  const m = map[status] || { tone: 'bg-slate-100 text-slate-700', label: status };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${m.tone}`}>
      {m.label}
    </span>
  );
};

function NewRequestSheet({ onClose, onCreated, token }) {
  const [services, setServices] = useState([]);
  const [state, setState] = useState('TX');
  const [urgency, setUrgency] = useState('routine');
  const [budget, setBudget] = useState('250');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [zip5, setZip5] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleService = (v) => {
    setServices((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  const submit = async () => {
    if (!first.trim() || !last.trim() || !dob) {
      toast.error('Patient first/last name + DOB are required'); return;
    }
    if (services.length === 0) {
      toast.error('Select at least one service'); return;
    }
    setSubmitting(true);
    try {
      const body = {
        ordered_services: services,
        state, urgency,
        funding_source: 'private_pay',
        private_pay_budget_cents: Math.round(parseFloat(budget || '0') * 100),
        patient: {
          first_name: first.trim(), last_name: last.trim(),
          dob,
          zip5: zip5.trim() || null,
        },
      };
      await axios.post(`${API}/family/match-requests`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Request created — see candidates below');
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not create request';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 200));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          data-testid="new-request-sheet">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-slate-900">New request</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"
                  data-testid="close-new-request">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="space-y-1">
          <Label>Services needed</Label>
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button key={s.v} type="button"
                      onClick={() => toggleService(s.v)}
                      aria-pressed={services.includes(s.v)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        services.includes(s.v)
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`service-${s.v}`}>
                {s.l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>State</Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger data-testid="state-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Urgency</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger data-testid="urgency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="priority">Priority</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="budget">Budget (USD)</Label>
            <div className="relative">
              <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="budget" value={budget} onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g,''))}
                      className="pl-7" placeholder="250" data-testid="budget-input" />
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Loved one</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="first">First name</Label>
              <Input id="first" value={first} onChange={(e) => setFirst(e.target.value)}
                      data-testid="first-name-input" />
            </div>
            <div>
              <Label htmlFor="last">Last name</Label>
              <Input id="last" value={last} onChange={(e) => setLast(e.target.value)}
                      data-testid="last-name-input" />
            </div>
            <div>
              <Label htmlFor="dob">DOB</Label>
              <Input id="dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                      data-testid="dob-input" />
            </div>
            <div>
              <Label htmlFor="zip">ZIP (optional)</Label>
              <Input id="zip" value={zip5}
                      onChange={(e) => setZip5(e.target.value.replace(/\D/g,'').slice(0,5))}
                      data-testid="zip-input" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-700">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={submitting}
                  className="px-5 py-2 text-sm rounded-full bg-slate-900 text-white disabled:opacity-50"
                  data-testid="submit-new-request">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
            Find providers
          </button>
        </div>
      </div>
    </div>
  );
}

function CandidatesSheet({ fmr, onClose, onSelect, token }) {
  const [trust, setTrust] = useState({});

  const loadTrust = useCallback(async (clinicianId) => {
    if (trust[clinicianId]) return;
    try {
      const res = await axios.get(`${API}/family/providers/${clinicianId}/trust`,
        { headers: { Authorization: `Bearer ${token}` } });
      setTrust((prev) => ({ ...prev, [clinicianId]: res.data }));
    } catch {
      // ignore — trust card is enrichment, not gating
    }
  }, [token, trust]);

  useEffect(() => {
    (fmr.candidates || []).slice(0, 6).forEach((c) => loadTrust(c.id));
  }, [fmr, loadTrust]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          data-testid="candidates-sheet">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Candidates</h2>
            <p className="text-xs text-slate-500 mt-1">
              {fmr.state} · {fmr.ordered_services.join(', ')}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"
                  data-testid="close-candidates">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {(fmr.candidates || []).length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No matches in {fmr.state} yet. Try again later — the network is growing weekly.
          </div>
        ) : (
          <ul className="space-y-3">
            {fmr.candidates.map((c) => {
              const t = trust[c.id];
              return (
                <li key={c.id} className="p-4 rounded-2xl border border-slate-200 hover:border-slate-300"
                    data-testid={`candidate-${c.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mt-0.5">
                      <Stethoscope className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold text-slate-900">{c.display}</span>
                        {typeof c.match_score === 'number' && c.match_score > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-700 tabular-nums"
                            data-testid={`match-score-${c.id}`}
                            title="Same transparency the hospital discharge planner sees"
                          >
                            <Sparkles className="w-3 h-3" /> {c.match_score}% match
                          </span>
                        )}
                        {(t?.credentialing_status || c.credentialing_status) && (
                          <CredentialingPill
                            status={t?.credentialing_status || c.credentialing_status}
                            label={t?.credentialing_label || c.credentialing_status}
                          />
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {c.state || '—'}
                        </span>
                        <span>{c.service_types?.slice(0, 3).join(', ')}</span>
                        {typeof t?.completed_visits_count === 'number' && (
                          <span>{t.completed_visits_count} completed visits</span>
                        )}
                      </div>
                      {Array.isArray(c.match_reasons) && c.match_reasons.length > 0 && (
                        <ul className="mt-2 space-y-1" data-testid={`match-reasons-${c.id}`}>
                          {c.match_reasons.slice(0, 3).map((r, i) => (
                            <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      )}
                      {t?.report_counts && (t.report_counts.open || 0) > 0 && (
                        <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {t.report_counts.open} open report(s)
                        </div>
                      )}
                    </div>
                    <button onClick={() => onSelect(c.id)}
                            className="text-sm px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                            data-testid={`select-${c.id}`}>
                      Select
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-xs text-slate-400 text-center pt-3 border-t border-slate-100">
          Selecting enters the same matching pipeline used by hospitals.
        </p>
      </div>
    </div>
  );
}
